/**
 * React Query hooks for project templates (non-admin).
 */

import { useQuery } from "@tanstack/react-query";
import { useWorkspace } from "../contexts/WorkspaceContext";
import {
  getTemplateShapeRecipes,
  getWorkspaceProjectTemplates,
  type ProjectTemplatesResponse,
} from "../services/naraDoor";

export const workspaceProjectTemplatesOptions = (workspaceId: string | undefined) => ({
  queryKey: ["workspaceProjectTemplates", workspaceId],
  queryFn: () => getWorkspaceProjectTemplates(workspaceId!),
  enabled: !!workspaceId,
});

// Same query key as the list: a member reads one template from the list they can already load
export const projectTemplateOptions = (workspaceId: string | undefined, templateId: string | undefined) => ({
  ...workspaceProjectTemplatesOptions(workspaceId),
  select: (data: ProjectTemplatesResponse) => data.templates.find((t) => t.id === templateId),
});

/**
 * Hook to fetch project templates available to a workspace.
 */
export function useWorkspaceProjectTemplates(workspaceId: string | undefined) {
  return useQuery(workspaceProjectTemplatesOptions(workspaceId));
}

/**
 * Hook to read a single project template of the active workspace by ID.
 */
export function useProjectTemplate(templateId: string | undefined) {
  const { activeWorkspace } = useWorkspace();
  return useQuery(projectTemplateOptions(activeWorkspace?.id, templateId));
}

/**
 * Hook to fetch the project recipes of one template of a workspace (the auto-project recipe pick).
 */
export function useTemplateShapeRecipes(workspaceId: string | undefined, templateId: string | undefined) {
  return useQuery({
    queryKey: ["templateShapeRecipes", workspaceId, templateId],
    queryFn: () => getTemplateShapeRecipes(workspaceId!, templateId!),
    enabled: !!workspaceId && !!templateId,
  });
}
